import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isBlank, notBlank } from "../../util/ext";
import Paper from "@material-ui/core/Paper";
import { useConfirm } from "../../common/use-confirm";
import {
  any,
  append,
  concat,
  difference,
  map,
  prop,
  propEq,
  reject,
  sortBy,
  without,
} from "ramda";
import Grid from "@material-ui/core/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import DialogContentText from "@material-ui/core/DialogContentText";
import { useCategories } from "../cspServices";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const CategoriesDialog = ({ open, project, onCancel, onApply }) => {
  const classes = useStyles();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);

  const categoriesResponse = useCategories();
  useEffect(() => {
    if (categoriesResponse.isSuccess) {
      if (notBlank(project.categories) && !project.useDefaultCategories) {
        setSelected(project.categories);
        const unused = reject(
          cat => any(propEq("categoryId", cat.categoryId), project.categories),
          categoriesResponse.data,
        );

        setAvailable(
          sortBy(prop("categoryId"), concat(project.categories, unused)),
        );
      } else {
        setAvailable(categoriesResponse.data);
      }
    }
  }, [
    categoriesResponse.isSuccess,
    categoriesResponse.data,
    project.categories,
    project.useDefaultCategories,
  ]);

  const handleClose = useCallback(() => {
    const previous =
      notBlank(project.categories) && !project.useDefaultCategories
        ? project.categories
        : [];
    if (isBlank(difference(selected, previous))) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected categories without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [
    selected,
    project.categories,
    project.useDefaultCategories,
    showConfirm,
    onCancel,
  ]);

  const handleApply = useCallback(() => {
    onApply(selected);
  }, [onApply, selected]);

  const handleSelected = cat => event => {
    if (event.target.checked) {
      setSelected(prev => append(cat, prev));
    } else {
      setSelected(prev => without([cat], prev));
    }
  };

  return (
    <Dialog open={open} maxWidth="xl" fullWidth>
      <DialogTitle id="add-submission-types-dialog-title">
        Update Project Categories
      </DialogTitle>
      <DialogContent>
        {categoriesResponse.isLoading && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            <Typography variant="h5">Loading Categories...</Typography>
            <PanelLoadingIndicator />
          </Box>
        )}
        <DialogContentText>
          {`Check the Categories that should allowed for this Project.`}
        </DialogContentText>
        <Paper>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              {map(
                cat => (
                  <Grid key={cat.categoryId} item md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            !project.useDefaultCategories &&
                            any(propEq("categoryId", cat.categoryId), selected)
                          }
                          onChange={handleSelected(cat)}
                          name={cat.categoryId}
                          color="primary"
                        />
                      }
                      label={cat.categoryName}
                    />
                  </Grid>
                ),
                available,
              )}
            </Grid>
          </Box>
        </Paper>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoriesDialog;
